import { z } from "zod";
import type { components } from "../../../api/generated";
import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import { isHttpClientError } from "../../../lib/http/errors";

const value = z.union([z.string(), z.number(), z.array(z.string()), z.null()]);
const rowSchema = z.object({
  source_row: z.number().int(), source_hotel_id: z.string(), hotel_name: z.string(),
  action: z.enum(["new", "update", "review"]), target_id: z.string().nullable(),
  patch: z.record(z.string(), value), before: z.record(z.string(), value),
  reason: z.string(), related_source_rows: z.array(z.number()).default([]),
  source_fields: z.record(z.string(), z.string()).default({}),
});
const batchSchema = z.object({
  batch_id: z.string(), file_name: z.string(), data_date: z.string(),
  status: z.enum(["preview", "completed"]), token: z.string().length(64),
  rows: z.array(rowSchema),
});
const recordsSchema = z.array(z.object({
  id: z.string(), batch_id: z.string(), source_hotel_id: z.string(),
  source_url: z.string().nullable(), created_at: z.string(), created_by: z.number(),
  snapshot_text: z.string(),
}));
export type ExternalStayBatch = components["schemas"]["ExternalStayBatch"];
export type ExternalStayPlanRow = components["schemas"]["ExternalStayPlanRow"];
const noRetry = { "axios-retry": { retries: 0 }, timeout: 120_000 } as const;
const root = "/resource-stays/external-import";

/** 只请求服务器选出的未判断行，已有确定身份与判断缓存不重复计费。 */
export async function analyzeExternalStayImport(id: string, token: string) {
  const response = await httpClient.post<ApiEnvelope<ExternalStayBatch>>(
    `${root}/${encodeURIComponent(id)}/analyze`, { token }, noRetry);
  return batchSchema.parse(response.data.data);
}


/** 上传统一酒店表和可选的已审核判断包，保存服务器预览。 */
export async function previewExternalStayImport(file: File, date: string, decisions?: File) {
  const form = new FormData();
  form.append("file", file);
  form.append("data_date", date);
  if (decisions) form.append("decisions", decisions);
  try {
    const response = await httpClient.post<ApiEnvelope<ExternalStayBatch>>(`${root}/preview`, form, noRetry);
    return batchSchema.parse(response.data.data);
  } catch (error) {
    if (isHttpClientError(error) && error.statusCode === 413) {
      throw new Error(
        "上传超过服务器限制：Excel 最大 10 MiB，判断包最大 20 MiB，总请求最大 32 MiB。请使用大小符合要求的配套文件；若仍报错，请联系管理员核对部署的上传限制。",
        { cause: error },
      );
    }
    throw error;
  }
}

/** 断线后读取原批次，不重新执行。 */
export async function loadExternalStayImport(id: string) {
  const response = await httpClient.get<ApiEnvelope<ExternalStayBatch>>(`${root}/${encodeURIComponent(id)}`);
  return batchSchema.parse(response.data.data);
}

/** 执行只发送预览摘要，禁止客户端提供更新补丁。 */
export async function executeExternalStayImport(id: string, token: string) {
  const response = await httpClient.post<ApiEnvelope<ExternalStayBatch>>(
    `${root}/${encodeURIComponent(id)}/execute`, { token }, noRetry);
  return batchSchema.parse(response.data.data);
}

/** 人工结论只更新服务器预览，不能直接写酒店。 */
export async function resolveExternalStayImport(id: string, payload: components["schemas"]["ExternalStayResolve"]) {
  const response = await httpClient.post<ApiEnvelope<ExternalStayBatch>>(
    `${root}/${encodeURIComponent(id)}/resolve`, payload, noRetry);
  return batchSchema.parse(response.data.data);
}

/** 读取不可编辑的来源记录。 */
export async function loadStayExternalRecords(spuId: string) {
  const response = await httpClient.get<ApiEnvelope<components["schemas"]["ExternalStayRecord"][]>>(
    `/resource-stays/${encodeURIComponent(spuId)}/external-records`);
  return recordsSchema.parse(response.data.data);
}
